import { Box, Typography, Skeleton, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors, inkOver } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { DashboardAgent } from '../services/responses/dashboard.response';

interface Props {
    data?: DashboardAgent[];
}

function initials(name: string): string {
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');
}

/// Três medidas por atendente em tabela: grandezas diferentes não cabem num eixo só.
export const AgentTable = ({ data }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const rows = data ?? [];
    const latestInteraction = Math.max(1, ...rows.map((row) => row.interactions));

    const cell = {
        padding: '8px 10px',
        fontSize: '0.8rem',
        borderBottom: `1px solid ${cc.border}`,
        whiteSpace: 'nowrap' as const,
    };

    const columns = [
        ['atendente', 'left'],
        ['leadsDoAtendente', 'right'],
        ['ganhos', 'right'],
        ['interacoes', 'right'],
        ['atividadesConcluidas', 'right'],
    ] as const;

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2,
            }}
        >
            <Typography variant="body2" fontWeight={700} sx={{ color: cc.textPrimary, mb: 0.3 }}>
                {t('crmDashboard.atendentes')}
            </Typography>
            <Typography variant="caption" sx={{ color: cc.textSecondary, display: 'block', mb: 1.5 }}>
                {t('crmDashboard.atendentesAjuda')}
            </Typography>

            {!data ? (
                <Skeleton variant="rounded" height={140} />
            ) : rows.length === 0 ? (
                <Typography
                    variant="caption"
                    sx={{ color: cc.textMuted, py: 5, display: 'block', textAlign: 'center' }}
                >
                    {t('crmDashboard.semAtendente')}
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            minWidth: 460,
                            borderCollapse: 'collapse',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        <thead>
                            <tr>
                                {columns.map(([key, align]) => (
                                    <Box
                                        key={key}
                                        component="th"
                                        sx={{
                                            ...cell,
                                            textAlign: align,
                                            color: cc.textSecondary,
                                            textTransform: 'uppercase',
                                            letterSpacing: '.06em',
                                            fontSize: '10px',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t(`crmDashboard.${key}`)}
                                    </Box>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.userId}>
                                    <Box component="td" sx={{ ...cell, color: cc.textPrimary }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                sx={{
                                                    width: 22,
                                                    height: 22,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    bgcolor: row.color || cc.accent,
                                                    color: inkOver(row.color || cc.accent),
                                                }}
                                            >
                                                {initials(row.name)}
                                            </Avatar>
                                            {row.name}
                                        </Box>
                                    </Box>

                                    <Box
                                        component="td"
                                        sx={{ ...cell, textAlign: 'right', color: cc.textPrimary }}
                                    >
                                        {row.leads}
                                    </Box>

                                    <Box
                                        component="td"
                                        sx={{ ...cell, textAlign: 'right', color: cc.textPrimary }}
                                    >
                                        {row.won}
                                    </Box>

                                    <Box
                                        component="td"
                                        sx={{ ...cell, textAlign: 'right', color: cc.textPrimary }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 1,
                                            }}
                                        >
                                            {/* A barra compara de relance quem trabalhou mais; o número exato fica ao lado. */}
                                            <Box
                                                sx={{
                                                    width: 64,
                                                    height: 6,
                                                    borderRadius: '3px',
                                                    background: cc.countBg,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: `${(row.interactions / latestInteraction) * 100}%`,
                                                        height: '100%',
                                                        background: cc.accent,
                                                    }}
                                                />
                                            </Box>
                                            {row.interactions}
                                        </Box>
                                    </Box>

                                    <Box
                                        component="td"
                                        sx={{ ...cell, textAlign: 'right', color: cc.textPrimary }}
                                    >
                                        {row.schedulesCompleted}
                                    </Box>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
